import {
  LayoutGrid,
  Search as SearchIcon,
  Folder,
  Users,
  Bookmark,
  LogOut,
  Loader2,
  Reply,
  Download,
  Mail,
  Linkedin,
  Chrome,
  ListTodo,
  ChevronDown,
  ChevronRight,
  PanelLeft,
  PanelLeftClose,
  ScrollText,
  BookOpen,
  Trash2,
  Send,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';
import { useUiStore, type AppView, type LinkedInTab } from '../../store/ui';
import { useMailStore } from '../../store/mail';
import { useAccounts } from '../../hooks/useAccounts';
import { useToReply } from '../../hooks/useToReply';
import { messagesApi } from '../../lib/api';
import { useAuth } from '../../store/auth';

// ----- Struttura di navigazione a voci + sottomenu -----
// Una voce può essere una vista diretta oppure un gruppo espandibile con sottovoci.
// Le sottovoci LinkedIn selezionano anche il tab interno della pagina LinkedIn.
type NavLeaf = {
  key: string;
  label: string;
  icon: LucideIcon;
  view: AppView;
  linkedinTab?: LinkedInTab;
};
type NavGroup = { key: string; label: string; icon: LucideIcon; children: NavLeaf[] };
type NavItem = NavLeaf | NavGroup;

const NAV: NavItem[] = [
  { key: 'dashboard', label: 'Cruscotto', icon: LayoutGrid, view: 'dashboard' },
  {
    key: 'posta',
    label: 'Posta',
    icon: Mail,
    children: [
      { key: 'list', label: 'Elenco', icon: Mail, view: 'list' },
      // DISATTIVATO (pannello di gestione, non webmail completa): { key: 'search', label: 'Ricerca', icon: SearchIcon, view: 'search' },
      // DISATTIVATO (pannello di gestione, non webmail completa): { key: 'folders', label: 'Cartelle', icon: Folder, view: 'folders' },
      { key: 'sent', label: 'Inviata', icon: Send, view: 'sent' },
      { key: 'deletion', label: 'In cancellazione', icon: Trash2, view: 'deletion' },
    ],
  },
  // Link al manuale utente interattivo (stessa vista raggiungibile dalla NavRail).
  { key: 'manuali', label: 'Manuale', icon: BookOpen, view: 'manuali' },
];
// NOTA ristrutturazione nav: Contatti/LinkedIn/Novità sono usciti da questa sidebar,
// che ora è il secondo livello della sola sezione Mail. Le sezioni si raggiungono dalla NavRail.

// Stato di espansione dei gruppi, persistito in localStorage 'wm-nav-groups'.
const GROUPS_KEY = 'wm-nav-groups';
function loadGroups(): Record<string, boolean> {
  try {
    const r = JSON.parse(localStorage.getItem(GROUPS_KEY) || 'null');
    if (r && typeof r === 'object') return r as Record<string, boolean>;
  } catch {
    /* localStorage non disponibile */
  }
  // Default: entrambi i gruppi aperti
  return { posta: true, linkedin: true };
}
function saveGroups(g: Record<string, boolean>) {
  try {
    localStorage.setItem(GROUPS_KEY, JSON.stringify(g));
  } catch {
    /* ignora errori di persistenza */
  }
}

/**
 * Sidebar "snella" con navigazione a voci + sottomenu:
 * Cruscotto · Posta (Elenco/Ricerca/Cartelle) · Contatti · LinkedIn (Estensione/Salvati/Coda).
 * I gruppi sono espandibili e lo stato è persistito in localStorage.
 * La gestione tag NON vive più qui: i tag si gestiscono dalla pagina Posta.
 * Può collassare (w-14, solo icone) tramite lo store UI (sidebarCollapsed).
 */
export function SlimSidebar() {
  const currentView = useUiStore((s) => s.currentView);
  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const linkedinTab = useUiStore((s) => s.linkedinTab);
  const openLinkedIn = useUiStore((s) => s.openLinkedIn);
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  const selectedAccountId = useMailStore((s) => s.selectedAccountId);
  const setSelectedAccount = useMailStore((s) => s.setSelectedAccount);
  const setSelectedMessage = useMailStore((s) => s.setSelectedMessage);
  const feedFilter = useMailStore((s) => s.feedFilter);
  const setFeedFilter = useMailStore((s) => s.setFeedFilter);

  const { data: toReply, isLoading: toReplyLoading } = useToReply();
  const [downloading, setDownloading] = useState(false);
  // Espansione gruppi (Posta / LinkedIn), persistita nel browser.
  const [groups, setGroups] = useState<Record<string, boolean>>(loadGroups);
  const toggleGroup = (key: string) =>
    setGroups((g) => {
      const next = { ...g, [key]: !g[key] };
      saveGroups(next);
      return next;
    });

  // Apre un messaggio della lista "Da rispondere": lo seleziona e porta al Feed
  // dove il MailViewer mostra il dettaglio (stesso meccanismo delle card Feed).
  function openToReply(id: string) {
    setSelectedMessage(id);
    setCurrentView('feed');
  }

  // Naviga verso una sottovoce/voce foglia (per LinkedIn imposta anche il tab).
  function goTo(leaf: NavLeaf) {
    if (leaf.view === 'linkedin') openLinkedIn(leaf.linkedinTab);
    else setCurrentView(leaf.view);
  }

  // Una foglia è attiva se la vista corrisponde (e per LinkedIn anche il tab).
  function isLeafActive(leaf: NavLeaf) {
    if (leaf.view === 'linkedin') return currentView === 'linkedin' && linkedinTab === leaf.linkedinTab;
    return currentView === leaf.view;
  }

  // Scarica l'export XLSX dei messaggi da rispondere (download via browser).
  async function handleDownloadXls() {
    if (downloading) return;
    setDownloading(true);
    try {
      await messagesApi.downloadToReplyXlsx();
    } catch {
      /* errore silenzioso: il download semplicemente non parte */
    } finally {
      setDownloading(false);
    }
  }

  const { data: accounts } = useAccounts();
  const { user, signOut } = useAuth();

  const hasAccounts = !!accounts && accounts.length > 0;

  const leafClass = (active: boolean, sub = false) =>
    'flex w-full items-center gap-2 rounded-md text-sm font-medium transition ' +
    (sub ? 'px-2.5 py-1.5 pl-7 text-[13px]' : 'px-2.5 py-1.5') +
    ' ' +
    (active
      ? 'bg-accent-500/15 text-accent-200'
      : 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100');

  return (
    <aside
      className={
        'flex flex-col border-r border-zinc-800/80 bg-zinc-950/30 transition-all duration-200 ' +
        (collapsed ? 'w-14' : 'w-56')
      }
    >
      {/* Pulsante toggle sidebar */}
      <div className={'flex ' + (collapsed ? 'justify-center px-2 pt-3' : 'justify-end px-3 pt-3')}>
        <button
          onClick={toggleSidebar}
          title={collapsed ? 'Espandi menu' : 'Comprimi menu'}
          className="rounded-md p-1.5 text-zinc-500 transition hover:bg-zinc-800/60 hover:text-zinc-200"
        >
          {collapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      {/* Navigazione a voci + sottomenu */}
      <nav className={'space-y-0.5 ' + (collapsed ? 'px-2 pt-2' : 'p-3')}>
        {collapsed
          ? /* Versione compatta: tutte le foglie come sole icone */
            NAV.flatMap((item) => ('children' in item ? item.children : [item])).map((leaf) => {
              const active = isLeafActive(leaf);
              const Icon = leaf.icon;
              return (
                <button
                  key={leaf.key}
                  onClick={() => goTo(leaf)}
                  title={leaf.label}
                  className={
                    'flex w-full items-center justify-center rounded-md py-2 transition ' +
                    (active
                      ? 'bg-accent-500/15 text-accent-200'
                      : 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')
                  }
                >
                  <Icon size={14} className={active ? 'text-accent-400' : 'text-zinc-500'} />
                </button>
              );
            })
          : NAV.map((item) => {
              if (!('children' in item)) {
                const active = isLeafActive(item);
                const Icon = item.icon;
                return (
                  <button key={item.key} onClick={() => goTo(item)} className={leafClass(active)}>
                    <Icon size={14} className={active ? 'text-accent-400' : 'text-zinc-500'} />
                    {item.label}
                  </button>
                );
              }
              // Gruppo espandibile: header + sottovoci
              const open = !!groups[item.key];
              const childActive = item.children.some((c) => isLeafActive(c));
              const Icon = item.icon;
              return (
                <div key={item.key}>
                  <button
                    onClick={() => toggleGroup(item.key)}
                    title={open ? `Comprimi ${item.label}` : `Espandi ${item.label}`}
                    className={
                      'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium transition ' +
                      (childActive
                        ? 'text-accent-200'
                        : 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')
                    }
                  >
                    <Icon size={14} className={childActive ? 'text-accent-400' : 'text-zinc-500'} />
                    <span className="flex-1 text-left">{item.label}</span>
                    {open ? (
                      <ChevronDown size={13} className="text-zinc-500" />
                    ) : (
                      <ChevronRight size={13} className="text-zinc-500" />
                    )}
                  </button>
                  {open && (
                    <div className="space-y-0.5 pt-0.5">
                      {item.children.map((leaf) => {
                        const active = isLeafActive(leaf);
                        const LeafIcon = leaf.icon;
                        return (
                          <button key={leaf.key} onClick={() => goTo(leaf)} className={leafClass(active, true)}>
                            <LeafIcon size={13} className={active ? 'text-accent-400' : 'text-zinc-500'} />
                            {leaf.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
      </nav>

      {/* Selettore account (solo espansa) */}
      {!collapsed && hasAccounts && (
        <div className="px-3 pb-2">
          <select
            value={selectedAccountId ?? ''}
            onChange={(e) => setSelectedAccount(e.target.value || null)}
            className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1 text-xs text-zinc-300 outline-none focus:border-accent-500/50"
          >
            <option value="">Tutti gli account</option>
            {accounts!.map((a) => (
              <option key={a.id} value={a.id}>
                {a.email}
              </option>
            ))}
          </select>
        </div>
      )}

      {collapsed ? (
        /* Versione compatta: sola icona "Da rispondere" (con badge) */
        <div className="flex flex-1 flex-col items-center gap-1 overflow-y-auto px-2 pt-2">
          <button
            type="button"
            onClick={() => {
              setFeedFilter(feedFilter.kind === 'toReply' ? { kind: 'none' } : { kind: 'toReply' });
              setCurrentView('feed');
            }}
            title="Da rispondere"
            className={
              'relative rounded-md p-2 transition hover:bg-zinc-800/60 ' +
              (feedFilter.kind === 'toReply' ? 'text-orange-400' : 'text-zinc-500 hover:text-zinc-300')
            }
          >
            <Reply size={15} />
            {toReply && toReply.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1rem] items-center justify-center rounded-full bg-orange-100 px-1 text-[8px] font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                {toReply.length}
              </span>
            )}
          </button>
        </div>
      ) : (
        /* Versione espansa: sezione "Da rispondere" (i tag si gestiscono nella pagina Posta) */
        <div className="flex-1 overflow-y-auto px-3">
          <div className="pb-2">
            <div className="flex items-center justify-between pb-1 pt-2">
              <button
                type="button"
                onClick={() => {
                  // Mostra nel Feed TUTTE le mail da rispondere (toggle del filtro).
                  setFeedFilter(feedFilter.kind === 'toReply' ? { kind: 'none' } : { kind: 'toReply' });
                  setCurrentView('feed');
                }}
                title="Mostra nel Feed tutte le mail da rispondere"
                className={
                  'flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider transition hover:text-zinc-300 ' +
                  (feedFilter.kind === 'toReply' ? 'text-orange-400' : 'text-zinc-500')
                }
              >
                <Reply size={10} /> Da rispondere
                {toReply && toReply.length > 0 && (
                  <span className="inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-orange-100 px-1 text-[9px] font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                    {toReply.length}
                  </span>
                )}
              </button>
              <button
                onClick={handleDownloadXls}
                disabled={downloading || !toReply || toReply.length === 0}
                title="Scarica XLS"
                className="flex items-center gap-1 text-zinc-500 transition hover:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {downloading ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Download size={12} />
                )}
                <span className="text-[10px]">XLS</span>
              </button>
            </div>

            {toReplyLoading ? (
              <div className="flex items-center justify-center py-3">
                <Loader2 size={14} className="animate-spin text-zinc-600" />
              </div>
            ) : toReply && toReply.length > 0 ? (
              <ul className="space-y-0.5">
                {toReply.map((m) => (
                  <li key={m.id}>
                    <button
                      onClick={() => openToReply(m.id)}
                      className="group flex w-full flex-col gap-0.5 rounded-md px-2 py-1 text-left transition hover:bg-zinc-800/60"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs font-medium text-zinc-300">
                          {m.from_name ?? m.from_address ?? '(sconosciuto)'}
                        </span>
                        {m.received_at && (
                          <span className="shrink-0 text-[10px] text-zinc-500">
                            {format(new Date(m.received_at), 'dd/MM')}
                          </span>
                        )}
                      </div>
                      <span className="truncate text-[11px] text-zinc-500">
                        {m.subject ?? '(senza oggetto)'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-1 py-2 text-[11px] text-zinc-600">
                Nessun messaggio da rispondere.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Footer utente */}
      <div className={'border-t border-zinc-800/80 ' + (collapsed ? 'flex justify-center p-3' : 'p-3')}>
        {collapsed ? (
          <div
            title={user?.email ?? undefined}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 text-xs font-semibold uppercase"
          >
            {user?.email?.[0] ?? '?'}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 text-xs font-semibold uppercase">
              {user?.email?.[0] ?? '?'}
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="truncate text-xs font-medium">{user?.email}</div>
              <div className="truncate text-[10px] text-zinc-500">
                {accounts?.length ?? 0} account collegati
              </div>
            </div>
            <button
              onClick={() => signOut()}
              title="Esci"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-300"
            >
              <LogOut size={12} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
